import {useAuthStore} from '@features/user/store/store.ts'
import {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle,
    ModalParagraph,
    ModalCloseButton
} from '@components/ui/modal-atoms'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {useScheduleAssignment} from '@features/assignment/queries/useScheduleAssignment'
import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {Controller, useForm} from 'react-hook-form'
import {z} from 'zod'
import {formatLocaleDate} from '@/dates.ts'
import {addDays, constructNow, format, isWeekend, startOfDay, startOfToday} from 'date-fns'
import {toZonedTime} from 'date-fns-tz'
import {zodResolver} from '@hookform/resolvers/zod'
import {DatePicker} from '@components/commons/date-picker'
import {TimePicker} from '@components/commons/time-picker'
import {formatHostAvailabilityDates, checkIsTimeDisabled, sortHostAvailabilityDates} from '@features/assignment/utils'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

const datesClassName =
    "flex flex-col gap-2 text-sm [&_h4]:text-neutral-600 [&_li]:font-medium [&_li]:before:content-['•_'] [&_p]:font-medium"

const ValidationSchema = z.object({
    date: z.date(),
    time: z.string().min(1)
})
type ValidationSchema = z.infer<typeof ValidationSchema>

export const ScheduleModal = ({
    assignment,
    serviceType,
    onClose
}: {
    assignment: Assignment
    serviceType: ServiceType
    onClose: () => void
}) => {
    const {t} = useTranslation()
    const adminAccessToken = useAuthStore(state => state.adminAccessToken)
    const timeZone = assignment.entity.timezone
    const minDate = timeZone ? startOfDay(toZonedTime(constructNow(startOfToday()), timeZone)) : startOfToday()
    const scheduleAssignmentMutation = useScheduleAssignment({
        assignmentId: assignment.id,
        options: {
            onSuccess: onClose
        }
    })
    const form = useForm<ValidationSchema>({
        resolver: zodResolver(ValidationSchema)
    })
    const selectedDate = form.watch('date')
    const selectedTime = form.watch('time')

    const handleSubmit = form.handleSubmit(values => {
        //date to send adapted to safari format too
        const dateToSend = format(
            new Date(`${format(values.date, 'yyyy-MM-dd')} ${values.time}`.replace(/-/g, '/')),
            'yyyy-MM-dd HH:mm:ss'
        )

        scheduleAssignmentMutation.mutate(dateToSend)
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-120" showCloseButton={false}>
                <ModalHeader>
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>
                            {t(`assignment:descriptions:accepted_dates_declined:schedule_title:${serviceType}` as TKey)}
                        </ModalTitle>
                        <ModalParagraph>
                            {t(
                                `assignment:descriptions:accepted_dates_declined:schedule_paragraph:${serviceType}` as TKey
                            )}
                        </ModalParagraph>
                    </div>
                    <div className="flex gap-4">
                        <Controller
                            name="date"
                            control={form.control}
                            render={({field}) => (
                                <DatePicker
                                    asModal
                                    triggerProps={{label: t('commons:date'), className: 'w-full'}}
                                    minDate={adminAccessToken ? undefined : minDate}
                                    maxDate={
                                        !adminAccessToken && serviceType == 'review' ? addDays(minDate, 7) : undefined
                                    }
                                    isDateDisabled={date => serviceType == 'review' && isWeekend(date)}
                                    formatter={date => format(date, 'yyyy-MM-dd')}
                                    mode="single"
                                    numMonths={1}
                                    selectedDates={field.value ? [field.value] : []}
                                    onDatesChange={dates => field.onChange(dates[0])}
                                />
                            )}
                        />
                        <Controller
                            name="time"
                            control={form.control}
                            render={({field}) => (
                                <TimePicker
                                    asModal
                                    triggerProps={{label: t('commons:time'), className: 'w-full'}}
                                    selectedTime={field.value}
                                    isTimeDisabled={time =>
                                        checkIsTimeDisabled({
                                            time,
                                            date: selectedDate,
                                            timeZone,
                                            isAdmin: !!adminAccessToken
                                        })
                                    }
                                    onSelectTime={time => field.onChange(time)}
                                />
                            )}
                        />
                    </div>
                    {assignment.old_date_shoot ? (
                        <div className={datesClassName}>
                            <h4>
                                {t(
                                    `assignment:descriptions:accepted_dates_declined:previous_scheduled_title:${serviceType}` as TKey
                                )}
                            </h4>
                            <p>{formatLocaleDate(assignment.old_date_shoot, 'PPPPp')}</p>
                        </div>
                    ) : assignment.quote && assignment.quote.host_availability_dates.length > 0 ? (
                        <div className={datesClassName}>
                            <h4>{t('assignment:descriptions:accepted_dates_declined:host_dates_title')}</h4>
                            <ul>
                                {sortHostAvailabilityDates(assignment.quote.host_availability_dates).map(date => (
                                    <li key={date.starting_time}>
                                        {formatHostAvailabilityDates(date.starting_time, date.ending_time)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        false
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="tertiary" disabled={scheduleAssignmentMutation.isPending}>
                        {t('commons:close')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={
                            scheduleAssignmentMutation.isPending ||
                            !form.formState.isValid ||
                            (!!selectedTime &&
                                checkIsTimeDisabled({
                                    time: selectedTime,
                                    date: selectedDate,
                                    timeZone,
                                    isAdmin: !!adminAccessToken
                                }))
                        }
                        onClick={handleSubmit}
                    >
                        {t('assignment:descriptions:schedule')}
                        {scheduleAssignmentMutation.isPending && <Spinner size={20} />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
